import React, {useState, useEffect, useMemo, useCallback} from 'react'
import {useIntl} from 'react-intl'
import {KTCard, KTCardBody, Table, Loading} from '../../../components'
import {CustomCellDouble} from '../../../components/Table/columns/CustomCellDouble'
import {CustomCell} from '../../../components/Table/columns/CustomCell'
import {CustomHeader} from '../../../components/Table/columns/CustomHeader'
import {ListHeader} from '../../../components/Table/header/ListHeader'
import {ListLoading} from '../../../components/Table/ListLoading'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {realEstateGetAll, realEstateExcel} from '../../../redux/slices/project.slice'
import {projectLookup, basicLookup} from '../../../redux/slices/lookup.slice'

import {useMemoizedStaticData} from '../../../utils/performanceOptimizations'

const selectRealEstateData = (state) => ({
  realEstate: state.project.realEstate,
  loading: state.project.loading,
})

const selectLookupData = (state) => ({
  projectLookupList: state.lookup.projectLookupList,
  EstateType: state.lookup.EstateType,
  EstateState: state.lookup.EstateState,
})

function List() {
  const intl = useIntl()
  const {realEstate, loading} = useSelector(selectRealEstateData)
  const {projectLookupList, EstateType, EstateState} = useSelector(selectLookupData)
  const dispatch = useDispatch()
  const pageKey = 'ESTATES'

  const [params, setParams] = useState()

  useEffect(() => {
    if (params) dispatch(realEstateGetAll(params))
  }, [dispatch, params])

  useEffect(() => {
    dispatch(projectLookup())
    dispatch(basicLookup('EstateType'))
    dispatch(basicLookup('EstateState'))
  }, [dispatch])

  const handleSorting = useCallback((item) => {
    setParams((prevState) => ({...prevState, sort: item && item.sort, order: item && item.order}))
  }, [])

  const handleSearch = useCallback((item) => {
    setParams((prevState) => ({...prevState, name: item}))
  }, [])

  const handlePagination = useCallback((pagination) => {
    setParams((prevState) => ({...prevState, ...pagination}))
  }, [])

  const handleExcelExport = useCallback(() => {
    dispatch(
      realEstateExcel({
        params,
        fileName: intl.formatMessage({id: 'BC.ESTATES'}),
      })
    )
  }, [dispatch, params, intl])

  const handleFilterSubmit = useCallback((values) => {
    setParams({...values})
  }, [])

  const columns = useMemo(
    () => [
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.NAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'name',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.BASESALEPRICE'})}
            className='min-w-125px'
          />
        ),
        accessor: 'baseSalePrice',
        Cell: ({...props}) => <CustomCell item={props} price />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.PROJECTNAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'projectName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.ESTATETYPENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'estateTypeName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.GROSSAREA'})}
            className='min-w-125px'
          />
        ),
        accessor: 'grossArea',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.NETAREA'})}
            className='min-w-125px'
          />
        ),
        accessor: 'netArea',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'REALESTATE.SEARCH.ESTATESTATENAME'})}
            className='min-w-125px'
          />
        ),
        accessor: 'estateStateName',
        Cell: ({...props}) => <CustomCell item={props} />,
      },
      {
        Header: (props) => (
          <CustomHeader
            tableProps={props}
            handleChangeSorting={handleSorting}
            title={intl.formatMessage({id: 'LIST.LASTMODIFIEDINFO'})}
            className='min-w-125px'
          />
        ),
        accessor: 'lastModifiedBy',
        Cell: ({...props}) => <CustomCellDouble item={props} double='lastModifiedDate' />,
      },
    ],
    [intl, handleSorting]
  )

  const actionButtons = useMemoizedStaticData(
    () => [
      {
        title: 'BUTTON.EXCEL',
        onClick: handleExcelExport,
        icon: '/images/icons/duotune/arrows/arr044.svg',
        variant: 'light-primary',
      },
      {
        title: 'FILTER',
        icon: '/images/icons/duotune/general/gen031.svg',
        variant: 'light-primary',
        list: [
          {
            title: 'REALESTATE.SEARCH.PROJECTNAME',
            data: projectLookupList,
            name: 'projectId',
          },
          {
            title: 'REALESTATE.SEARCH.ESTATETYPENAME',
            data: EstateType,
            name: 'estateTypeId',
          },
          {
            title: 'REALESTATE.SEARCH.ESTATESTATENAME',
            data: EstateState,
            name: 'estateStateId',
          },
        ],
      },
    ],
    [handleExcelExport, projectLookupList, EstateType, EstateState]
  )

  return (
    <>
      <PageTitle>{intl.formatMessage({id: 'BC.ESTATES'})}</PageTitle>
      <KTCard>
        <ListHeader
          search={handleSearch}
          actionButtons={actionButtons}
          onSubmit={handleFilterSubmit}
          pages={pageKey}
        />
        <KTCardBody>
          {!realEstate && <ListLoading />}
          {realEstate && (
            <Table
              items={realEstate}
              cols={columns}
              handlePagination={handlePagination}
              paddless
              pages={pageKey}
            />
          )}
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {List}
